"use client"

import { CLOUDINARY_CLOUD } from "@/lib/cloudinary";
import { useState } from "react"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { z } from "zod"
import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import {
  Field,
  FieldDescription,
  FieldGroup,
  FieldLabel,
} from "@/components/ui/field"
import { Input } from "@/components/ui/input"
import Image from "next/image"
import { toast } from "sonner"
import axios from "axios"
import { trackLead } from "@/lib/meta-pixel"
import { useSpamGuard } from "@/components/spam/use-spam-guard"

const propietariosFormSchema = z.object({
  name: z.string().min(1, "Por favor ingresa tu nombre completo"),
  email: z.string().email("Por favor ingresa un email válido"),
  phone: z.string().min(1, "Por favor ingresa tu teléfono"),
  street: z.string().min(1, "Por favor ingresa la dirección"),
})

type PropietariosFormData = z.infer<typeof propietariosFormSchema>

interface PropietariosFormProps extends React.ComponentProps<"div"> {
  transparent?: boolean;
  hideImage?: boolean;
}

export function PropietariosForm({
  className,
  transparent = false,
  hideImage = false,
  ...props
}: PropietariosFormProps) {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const spam = useSpamGuard()

  const { register, handleSubmit, formState: { errors, isValid }, reset } = useForm<PropietariosFormData>({
    resolver: zodResolver(propietariosFormSchema),
    mode: "onChange",
    defaultValues: {
      name: "",
      email: "",
      phone: "",
      street: "",
    },
  })

  const onSubmit = async (data: PropietariosFormData) => {
    if (!spam.ready) {
      toast.error('Por favor completa la verificación de seguridad antes de enviar.')
      return
    }
    setIsSubmitting(true)
    try {
      await axios.post('/api/send-propietarios-email', {
        ...data,
        ...spam.getSpamPayload(),
      })

      // Track Meta Pixel Lead event
      trackLead('propietarios_form')

      toast.success('Mensaje envíado')

      // Reset form
      reset()
      spam.reset()
    } catch (error) {
      console.error("Error sending email:", error)
      toast.error('Error al enviar el mensaje. Por favor, inténtalo de nuevo.')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className={cn("flex flex-col gap-6 w-full", className)} {...props}>
      <Card className={cn(
        "overflow-hidden p-0 border-0 w-full",
        transparent ? "bg-transparent" : "bg-white"
      )}>
        <CardContent className={cn(
          "grid p-0",
          hideImage ? "md:grid-cols-1" : "md:grid-cols-2 min-h-[600px] md:h-[600px]"
        )}>
          {!hideImage && (
            <div className="bg-muted relative hidden md:block h-full">
            <Image
              src={`https://res.cloudinary.com/${CLOUDINARY_CLOUD}/image/upload/c_scale,w_2000/q_auto/f_auto/person/website/Person_Febrero_203_i5nkss_1_qxbcbt.jpg`}
              alt="Modern house"
              fill
              className="object-cover"
            />
            <div className="absolute bottom-0 left-0 right-0 px-4 sm:px-6 pt-8 sm:pt-12 pb-4 sm:pb-6 bg-gradient-to-t from-black/95 via-black/60 to-transparent">
              <p className="text-white text-sm sm:text-base leading-relaxed">
               Trabajamos de manera personal, como si fuéramos familia, para encontrar a ese alguien que pueda llamar hogar a tu propiedad. Tu no te preocupes por nada más que por cerrar el trato y recibir tus ingresos.
              </p>

            </div>
          </div>
          )}
          <form onSubmit={handleSubmit(onSubmit)} className={cn(
            "p-6 md:py-8 md:px-10 h-full flex flex-col overflow-hidden",
            transparent ? "text-white" : "text-gray-900"
          )}>
            {spam.fields}
            <div className="w-full max-w-md mx-auto flex flex-col h-full min-h-0">
              {/* Fixed Header */}
              {!transparent && (
                <div className="flex flex-col text-left mb-6 flex-shrink-0">
                  <p className="text-xs sm:text-sm text-gray-500 mb-2 leading-6">
                    Vende o alquila con Person Inmobiliaria
                  </p>
                  <h1 className="text-lg sm:text-xl text-gray-900">Encontramos a la persona ideal para comprar o alquilar tu propiedad</h1>
                </div>
              )}

              {/* Scrollable Content */}
              <div className="flex-1 overflow-y-auto min-h-0 pr-2">
                <div className="flex items-center justify-center min-h-full py-4">
                  <FieldGroup className="w-full">
                    <Field>
                      <FieldLabel htmlFor="name" className={cn(
                        "text-xs",
                        transparent ? "text-white" : "text-gray-800"
                      )}>
                        Nombre
                      </FieldLabel>
                      <Input
                        id="name"
                        type="text"
                        placeholder="Juan Pérez"
                        {...register("name")}
                        className={cn(
                          "!text-xs placeholder:text-gray-500 border focus-visible:ring-[#1A3043]/30",
                          transparent 
                            ? "!text-gray-900 !bg-white border-white/30 placeholder:text-gray-500 focus-visible:ring-white/30" 
                            : "text-gray-900 border-gray-200"
                        )}
                      />
                      {errors.name && (
                        <p className={cn(
                          "text-xs mt-1",
                          transparent ? "text-red-300" : "text-red-500"
                        )}>{errors.name.message}</p>
                      )}
                    </Field>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <Field>
                        <FieldLabel htmlFor="email" className={cn(
                          "text-xs",
                          transparent ? "text-white" : "text-gray-800"
                        )}>
                          Email
                        </FieldLabel>
                        <Input
                          id="email"
                          type="email"
                          placeholder="tu@email.com"
                          {...register("email")}
                          className={cn(
                            "!text-xs placeholder:text-gray-500 border focus-visible:ring-[#1A3043]/30",
                            transparent 
                              ? "!text-gray-900 !bg-white border-white/30 placeholder:text-gray-500 focus-visible:ring-white/30" 
                              : "text-gray-900 border-gray-200"
                          )}
                        />
                        {errors.email && (
                          <p className={cn(
                            "text-xs mt-1",
                            transparent ? "text-red-300" : "text-red-500"
                          )}>{errors.email.message}</p>
                        )}
                      </Field>
                      <Field>
                        <FieldLabel htmlFor="phone" className={cn(
                          "text-xs",
                          transparent ? "text-white" : "text-gray-800"
                        )}>
                          Teléfono
                        </FieldLabel>
                        <Input
                          id="phone"
                          type="tel"
                          placeholder="+34 600 000 000"
                          {...register("phone")}
                          className={cn(
                            "!text-xs placeholder:text-gray-500 border focus-visible:ring-[#1A3043]/30",
                            transparent 
                              ? "!text-gray-900 !bg-white border-white/30 placeholder:text-gray-500 focus-visible:ring-white/30" 
                              : "text-gray-900 border-gray-200"
                          )}
                        />
                        {errors.phone && (
                          <p className={cn(
                            "text-xs mt-1",
                            transparent ? "text-red-300" : "text-red-500"
                          )}>{errors.phone.message}</p>
                        )}
                      </Field>
                    </div>
                    <Field>
                      <FieldLabel htmlFor="street" className={cn(
                        "text-xs",
                        transparent ? "text-white" : "text-gray-800"
                      )}>
                        Dirección
                      </FieldLabel>
                      <Input
                        id="street"
                        type="text"
                        placeholder="Calle Ejemplo, 123"
                        {...register("street")}
                        className={cn(
                          "!text-xs placeholder:text-gray-500 border focus-visible:ring-[#1A3043]/30",
                          transparent 
                            ? "!text-gray-900 !bg-white border-white/30 placeholder:text-gray-500 focus-visible:ring-white/30" 
                            : "text-gray-900 border-gray-200"
                        )}
                      />
                      {errors.street && (
                        <p className={cn(
                          "text-xs mt-1",
                          transparent ? "text-red-300" : "text-red-500"
                        )}>{errors.street.message}</p>
                      )}
                    </Field>
                  </FieldGroup>
                </div>
              </div>

              {/* Fixed Footer */}
              <div className={cn(
                "flex-shrink-0 mt-6 pt-6 border-t",
                transparent ? "border-white/20" : "border-gray-100"
              )}>
                <div className="flex gap-3 mb-1">
                  <Button
                    type="submit"
                    disabled={!isValid || isSubmitting || !spam.ready}
                    className="flex-1 text-xs bg-[#1A3043] hover:bg-[#0A3043] text-white cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? "Enviando..." : "Enviar"}
                  </Button>
                </div>

                <div className="h-[28px] flex items-center justify-center">
                  <FieldDescription className={cn(
                    "text-center text-xs",
                    transparent ? "text-white/80" : "text-gray-700"
                  )}>
                    Al continuar, aceptas nuestros{" "}
                    <a
                      href="#"
                      className={cn(
                        "underline underline-offset-2 hover:underline",
                        transparent ? "text-white" : ""
                      )}
                    >
                      Términos de Servicio
                    </a>
                    .
                  </FieldDescription>
                </div>
              </div>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  )
}

